import React, { useCallback, useEffect, useState } from 'react'

import { delay } from 'lodash'

import { useQuery } from 'utils/hooks'

import Box from 'components_new/atoms/Box'

import NavigationItem from 'components_new/molecules/NavigationItem'
import SidebarDialog from 'components_new/molecules/SidebarDialog'

import Actions from './Actions'
import Content from './Content'
import Hero from './Hero'

import { WidgetType, TCoreWidgetSettingsLoaded } from 'types/GlobalWidget'

import { Step, TMetricDialogVariant, buildSteps } from './lib'
import { useTranslation } from 'language/translator'

import { ISelectMetric, TMetricSettings } from 'types/GlobalMetric'
import { useMetric } from 'redux/hooks/Metrics'

export interface MetricsDialogProps {
  availableSteps: Step[]
  title: string
  handleAddOther?: (
    type: WidgetType.TEXT | WidgetType.COMMENT | WidgetType.IMAGE
  ) => void
  handleOnAdd: (metric: ISelectMetric, settings: TMetricSettings) => void
  onClose: () => void
  open: boolean
  creating: boolean
  sx?: object
  variant: TMetricDialogVariant
  widget?: TCoreWidgetSettingsLoaded
}

const DEFAULT_SETTINGS_STATE: TMetricSettings = {
  rolling_sum: null,
  distribution: null,
  slices: [],
  variant_kpi_template_id: null
}

const MetricsDialog = (props: MetricsDialogProps) => {
  const {
    availableSteps,
    creating,
    handleAddOther,
    handleOnAdd,
    onClose,
    open,
    sx,
    title,
    variant,
    widget
  } = props
  const { t } = useTranslation()

  const { query, clearQuery } = useQuery()

  const [selectedMetric, setSelectedMetric] = useState<string | null>(null)
  const [tab, setTab] = useState<'metrics' | 'other'>('metrics')
  const [search, setSearch] = useState<string | null>(null)
  const [subcategory, setSubcategory] = useState<string | null>(null)
  const [category, setCategory] = useState<string | null>(null)
  const [settings, setSettings] = useState<TMetricSettings>(
    DEFAULT_SETTINGS_STATE
  )

  const metric = useMetric(selectedMetric, settings.variant_kpi_template_id)

  const [scrollToTop, setScrollToTop] = useState<() => void>(() => () => {})
  const bindScrollHandler = useCallback((fn: () => void) => {
    setScrollToTop(() => fn)
  }, [])

  useEffect(() => {
    if (open) {
      setSelectedMetric(null)
      setTab('metrics')
      setSearch('')
      setSubcategory(null)
      setCategory(null)
      setSettings(DEFAULT_SETTINGS_STATE)
      setActiveStep(0)
    }
  }, [open])

  useEffect(() => {
    scrollToTop()
  }, [query])

  // Pre-select the entity's default slice (if one is declared) when a
  // metric is picked.
  useEffect(() => {
    if (!metric || metric.slices.available.length === 0) return
    if (settings.slices.length > 0) return

    const defaultSlice = metric.slices.available.find((s) => s.is_default)

    if (defaultSlice) {
      setSettings((prev) => ({ ...prev, slices: [defaultSlice.id] }))
    }
  }, [metric])

  const steps = buildSteps(metric, availableSteps)

  const [activeStep, setActiveStep] = useState(0)
  const currentStep: Step = steps[activeStep]

  const handleSetActiveStep = (value: number) => {
    if (value === -1) {
      setSelectedMetric(null)
      setSettings(DEFAULT_SETTINGS_STATE)
      setActiveStep(0)
    } else {
      setActiveStep(value)
    }
  }

  const singleRequired = metric?.slices.selection_mode === 'single_required'
  const requredSliceMissing = singleRequired && settings.slices.length === 0

  const handleCreate = () => {
    if (!metric) return
    if (requredSliceMissing) return

    handleOnAdd(metric, settings)
  }

  const handleOnClose = () => {
    onClose()
    delay(clearQuery, 200)
  }

  return (
    <SidebarDialog
      bindScrollToTop={bindScrollHandler}
      disableOverflow={!Boolean(metric)}
      fullHeight={true}
      fullWidth={true}
      hero={
        <Hero
          tab={tab}
          metric={metric}
          search={search}
          setSearch={setSearch}
          category={category}
          setCategory={setCategory}
          subcategory={subcategory}
          setSubcategory={setSubcategory}
          activeStep={activeStep}
          steps={steps}
        />
      }
      maxWidth={metric ? 'md' : 'xl'}
      sx={sx}
      title={title}
      onClose={handleOnClose}
      open={open}
      actions={
        <Actions
          creating={creating}
          stepsCount={steps.length}
          activeStep={activeStep}
          metricSelected={!!metric}
          createDisabled={requredSliceMissing}
          onClose={onClose}
          onCreate={handleCreate}
          onChangeStep={handleSetActiveStep}
        />
      }
      leftSidebar={
        handleAddOther && !metric
          ? {
              content: (
                <Box>
                  <NavigationItem
                    active={tab === 'metrics'}
                    item={{
                      icon: {
                        name: 'Metrics'
                      },
                      onClick: () => setTab('metrics'),
                      title: t('metrics', 'metrics')
                    }}
                  />

                  <NavigationItem
                    active={tab === 'other'}
                    item={{
                      icon: {
                        name: 'Widgets'
                      },
                      onClick: () => setTab('other'),
                      title: t('global', 'tools')
                    }}
                  />
                </Box>
              ),
              size: 'small'
            }
          : undefined
      }
    >
      <Content
        category={category}
        currentStep={currentStep}
        handleAddOther={handleAddOther}
        metric={metric}
        search={search}
        setSelectedMetric={(id: string, defaultVariant: string | null) => {
          setSettings((prev) => ({
            ...prev,
            variant_kpi_template_id: defaultVariant
          }))
          setSelectedMetric(id)
        }}
        settings={settings}
        setSettings={setSettings}
        variant={variant}
        subcategory={subcategory}
        widget={widget}
        tab={tab}
      />
    </SidebarDialog>
  )
}

export default MetricsDialog
